import React, { useState, useRef, useEffect } from 'react';
import { FaMicrophone, FaFileAudio, FaPlayCircle, FaRegFileAlt, FaRegListAlt, FaClipboard, FaFileWord, FaFilePdf, FaEllipsisV } from 'react-icons/fa';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const QAAudioTranscriber = () => {
  const fileInputRef = useRef();
  const [progress, setProgress] = useState(0);
  const [transcription, setTranscription] = useState('');
  const [analysis, setAnalysis] = useState('');
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [fileName, setFileName] = useState('');
  const [transcriptionDropdownOpen, setTranscriptionDropdownOpen] = useState(false);
  const [analysisDropdownOpen, setAnalysisDropdownOpen] = useState(false);
  
  const transcriptionDropdownRef = useRef(null);
  const analysisDropdownRef = useRef(null);

  useEffect(() => {
    setIsLoaded(false);
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (transcriptionDropdownRef.current && !transcriptionDropdownRef.current.contains(event.target)) {
        setTranscriptionDropdownOpen(false);
      }
      if (analysisDropdownRef.current && !analysisDropdownRef.current.contains(event.target)) {
        setAnalysisDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const chunkAudioFile = async (file, maxChunkDuration = 60) => {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const buf = await file.arrayBuffer();
    const decoded = await audioCtx.decodeAudioData(buf);
    const chunkSize = maxChunkDuration * decoded.sampleRate;
    const chunks = [];

    for (let start = 0; start < decoded.length; start += chunkSize) {
      const end = Math.min(start + chunkSize, decoded.length);
      const chunk = audioCtx.createBuffer(
        decoded.numberOfChannels,
        end - start,
        decoded.sampleRate
      );
      for (let ch = 0; ch < decoded.numberOfChannels; ch++) {
        const seg = decoded.getChannelData(ch).subarray(start, end);
        chunk.copyToChannel(seg, ch);
      }
      chunks.push(chunk);
    }
    return chunks;
  };

  const audioBufferToWavBlob = (audioBuffer) => {
    const numChan = audioBuffer.numberOfChannels;
    const length = audioBuffer.length * numChan * 2 + 44;
    const buffer = new ArrayBuffer(length);
    const view = new DataView(buffer);

    const writeStr = (off, str) => {
      for (let i = 0; i < str.length; i++) view.setUint8(off + i, str.charCodeAt(i));
    };

    writeStr(0, 'RIFF');
    view.setUint32(4, length - 8, true);
    writeStr(8, 'WAVE');
    writeStr(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChan, true);
    view.setUint32(24, audioBuffer.sampleRate, true);
    view.setUint32(28, audioBuffer.sampleRate * numChan * 2, true);
    view.setUint16(32, numChan * 2, true);
    view.setUint16(34, 16, true);
    writeStr(36, 'data');
    view.setUint32(40, audioBuffer.length * numChan * 2, true);

    let offset = 44;
    for (let i = 0; i < audioBuffer.length; i++) {
      for (let ch = 0; ch < numChan; ch++) {
        let sample = Math.max(-1, Math.min(1, audioBuffer.getChannelData(ch)[i]));
        sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset, sample, true);
        offset += 2;
      }
    }
    return new Blob([view], { type: 'audio/wav' });
  };

  const transcribeChunk = async (blob) => {
    const form = new FormData();
    form.append('file', blob);
    const res = await fetch('http://localhost:5000/api/qaaudio/transcribe-chunk', { method: 'POST', body: form });
    if (!res.ok) throw new Error('Chunk transcription failed');
    return res.json();
  };
  
  const analyzeTranscriptionForQA = async (fullText) => {
    const res = await fetch('http://localhost:5000/api/qaaudio/analyze-qa', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fullText }),
    });
    if (!res.ok) throw new Error('Failed to analyze transcription for QA');
    const data = await res.json();
    return data.analysis;
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
    }
  };

  const handleUpload = async () => {
    const file = fileInputRef.current.files[0];
    if (!file) return alert('Please select a file.');

    setLoading(true);
    setProgress(0);
    let fullText = '';

    try {
      const audioChunks = await chunkAudioFile(file);
      for (let i = 0; i < audioChunks.length; i++) {
        const wavBlob = audioBufferToWavBlob(audioChunks[i]);
        const { segments } = await transcribeChunk(wavBlob);
        segments.forEach(s => {
          const start = new Date(s.start * 1000).toISOString().substr(11, 8);
          const end = new Date(s.end * 1000).toISOString().substr(11, 8);
          fullText += `[${start} - ${end}]:\n${s.text.trim()}\n\n`;
        });
        setProgress(Math.round(((i + 1) / audioChunks.length) * 100));
      }

      const qaAnalysis = await analyzeTranscriptionForQA(fullText);
      setTranscription(fullText);
      setAnalysis(qaAnalysis);
      
      toast.success('QA Analysis Complete', {
        description: `Audio file "${fileName}" has been successfully transcribed and analyzed.`,
        duration: 5000
      });
      
    } catch (err) {
      console.error(err);
      alert('An error occurred. Check console.');
      toast.error('QA Analysis Failed', {
        description: 'An error occurred during the transcription and analysis process.',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast.success(`${type} Copied`, {
          description: `${type} has been copied to clipboard.`,
          duration: 3000
        });
      },
      (err) => {
        console.error('Could not copy text: ', err);
        toast.error('Copy Failed', {
          description: 'An error occurred while copying to clipboard.',
          duration: 3000
        });
      }
    );
  };

  const downloadAsWord = (content, filename) => {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${filename}</title>
      </head>
      <body>
        <pre style="white-space: pre-wrap; font-family: Arial, sans-serif;">${content}</pre>
      </body>
      </html>
    `;
    
    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`${filename} Downloaded`, {
      description: `${filename} has been downloaded as a Word document.`,
      duration: 3000
    });
  };

  const downloadAsPDF = (content, filename) => {
    try {
      const doc = new jsPDF();
      
      doc.setFontSize(16);
      doc.text(filename, 20, 20);
      
      const now = new Date();
      doc.setFontSize(10);
      doc.text(`Generated on: ${now.toLocaleString()}`, 20, 30);
      
      const contentLines = content.split('\n');
      
      doc.setFontSize(11);
      let yPos = 40;
      const maxWidth = 170;
      
      for (let i = 0; i < contentLines.length; i++) {
        const line = contentLines[i];
        if (line.trim() === '') {
          yPos += 5;
          continue;
        }
        
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        
        const splitText = doc.splitTextToSize(line, maxWidth);
        doc.text(splitText, 20, yPos);
        yPos += 6 * splitText.length;
      }
      
      doc.save(`${filename}.pdf`);
      
      toast.success(`${filename} Downloaded`, {
        description: `${filename} has been downloaded as a PDF document.`,
        duration: 3000
      });
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.error('PDF Generation Failed', {
        description: 'An error occurred while generating the PDF.',
        duration: 3000
      });
    }
  };

  return (
    <div className="w-full h-full flex flex-col p-3 md:p-6 lg:p-8">
      <div className={`mb-4 transition-all duration-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <h1 className="text-xl font-bold text-gray-800 dark:text-white mb-1 flex items-center">
          <FaMicrophone className="mr-2" />
          QA Audio Transcriber
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Upload audio files for automatic transcription and quality assurance analysis.
        </p>
      </div>

      <div className={`bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md transition-all duration-500 delay-100 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="rounded-t-lg px-3 py-2 bg-black dark:bg-uasYellow text-white dark:text-black border-b flex items-center justify-between">
          <h3 className="text-sm font-bold flex items-center">
            <FaFileAudio className="mr-1" />
            Audio Processing
          </h3>
        </div>

        <div className="p-3">
          <div className="mb-3">
            <label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
              Audio File
            </label>
            <div className="flex items-center space-x-2">
              <div className="relative flex-grow">
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  accept="audio/*" 
                  className="hidden"
                  onChange={handleFileChange}
                />
                <div className="flex items-center">
                  <div className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-300 text-xs rounded-lg w-full p-2 flex items-center">
                    <FaFileAudio className="mr-2 text-gray-500 dark:text-gray-400" />
                    <span className="truncate">{fileName || "No file selected"}</span>
                  </div>
                  <button 
                    onClick={() => fileInputRef.current.click()}
                    className="ml-2 px-3 py-2 text-xs font-medium text-white dark:text-black bg-gray-700 dark:bg-uasYellow rounded-lg hover:bg-gray-800 dark:hover:bg-yellow-500 focus:outline-none"
                  >
                    Browse
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center w-full">
            <button
              onClick={handleUpload}
              disabled={loading}
              className="relative inline-flex items-center justify-center overflow-hidden p-0.5 text-xs font-medium rounded-lg group bg-gradient-to-br from-black to-gray-800 dark:from-uasYellow dark:to-yellow-500 "
            >
              <span className="relative w-full px-3 py-2 transition-all ease-in duration-75 bg-black text-white dark:bg-transparent dark:text-black rounded-md group-hover:bg-opacity-0 group-hover:text-white dark:group-hover:text-black flex items-center justify-center">
                <FaPlayCircle className="mr-1" />
                {loading ? 'Processing...' : 'Start QA Analysis'}
              </span>
            </button>
          </div>

          {loading && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Processing audio...</span>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div 
                  className="bg-black dark:bg-uasYellow h-1.5 rounded-full transition-all duration-300" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 flex-grow" style={{ minHeight: "500px", height: "calc(100vh - 200px)" }}>        <div className={`bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md transition-all duration-500 delay-200 flex flex-col h-full ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="rounded-t-lg px-3 py-2 bg-black dark:bg-uasYellow text-white dark:text-black border-b flex items-center justify-between">
            <h3 className="text-sm font-bold flex items-center">
              <FaRegFileAlt className="mr-1" />
              Transcription
            </h3>
            {transcription && (
              <div className="relative" ref={transcriptionDropdownRef}>
                <button 
                  onClick={() => setTranscriptionDropdownOpen(!transcriptionDropdownOpen)}
                  className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                >
                  <FaEllipsisV size={14} />
                </button>
                {transcriptionDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
                    <div className="py-1">
                      <button 
                        onClick={() => copyToClipboard(transcription, 'Transcription')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <FaClipboard className="mr-2" />
                        Copy to Clipboard
                      </button>
                      <button 
                        onClick={() => downloadAsWord(transcription, 'Transcription')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <FaFileWord className="mr-2" />
                        Download as Word
                      </button>
                      <button 
                        onClick={() => downloadAsPDF(transcription, 'Transcription')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <FaFilePdf className="mr-2" />
                        Download as PDF
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="p-3 flex-grow flex flex-col">
            {transcription ? (
              <div className="relative h-full w-full">
                <pre className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-xs text-gray-800 dark:text-gray-200 overflow-y-scroll border border-gray-200 dark:border-gray-700 whitespace-pre-wrap absolute inset-0">{transcription}</pre>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 text-center flex items-center justify-center h-full">
                Transcription will appear here after processing
              </div>
            )}
          </div>
        </div>

        <div className={`bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md transition-all duration-500 delay-300 flex flex-col h-full ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="rounded-t-lg px-3 py-2 bg-black dark:bg-uasYellow text-white dark:text-black border-b flex items-center justify-between">
            <h3 className="text-sm font-bold flex items-center">
              <FaRegListAlt className="mr-1" />
              QA Analysis Report
            </h3>
            {analysis && (
              <div className="relative" ref={analysisDropdownRef}>
                <button 
                  onClick={() => setAnalysisDropdownOpen(!analysisDropdownOpen)}
                  className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                >
                  <FaEllipsisV size={14} />
                </button>
                {analysisDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
                    <div className="py-1">
                      <button 
                        onClick={() => copyToClipboard(analysis, 'QA Analysis')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <FaClipboard className="mr-2" />
                        Copy to Clipboard
                      </button>
                      <button 
                        onClick={() => downloadAsWord(analysis, 'QA_Analysis')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <FaFileWord className="mr-2" />
                        Download as Word
                      </button>
                      <button 
                        onClick={() => downloadAsPDF(analysis, 'QA_Analysis')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <FaFilePdf className="mr-2" />
                        Download as PDF
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="p-3 flex-grow flex flex-col">
            {analysis ? (
              <div className="relative h-full w-full">
                <pre className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-xs text-gray-800 dark:text-gray-200 overflow-y-scroll border border-gray-200 dark:border-gray-700 whitespace-pre-wrap absolute inset-0">{analysis}</pre>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 text-center flex items-center justify-center h-full">
                QA analysis will appear here after processing
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QAAudioTranscriber;